import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="sf-card py-8 px-4 sm:px-10">
              <div className="text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-neon-pink" />
                <h2 className="mt-4 text-lg font-bold text-fg-bright">
                  Er ging iets mis
                </h2>
                <p className="mt-2 text-sm text-fg-muted">
                  Er is een onverwachte fout opgetreden. We hebben dit gelogd
                  en kijken ernaar.
                </p>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={this.handleReset}
                  className="sf-btn-secondary flex-1 px-4 py-2.5 text-sm"
                >
                  Probeer opnieuw
                </button>
                <button
                  onClick={this.handleReload}
                  className="sf-btn-primary flex-1 px-4 py-2.5 text-sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Pagina herladen
                </button>
              </div>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm font-medium text-fg-muted hover:text-fg-bright">
                    Error details (development only)
                  </summary>
                  <div className="mt-2 p-3 bg-white/[.04] border border-white/[.08] rounded-xl">
                    <pre className="text-xs text-fg-body whitespace-pre-wrap break-words">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
